// src/tiles/TileShell.jsx
import React from 'react';
import {
  Card, CardContent, Stack, Typography, Divider, IconButton, Tooltip, Box, Dialog, DialogTitle, DialogContent
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import ShareIcon from '@mui/icons-material/Share';
import CategoryPill from './CategoryPill';
import { Link } from 'react-router-dom'; // ⬅️ NEW

export default function TileShell({
  typePill,           // 'job' | 'trigger' | 'solution-prev' | ...
  variantPill,        // <VariantPill/> element (optional)
  title,
  justification,      // string (optional)
  primaryScore,       // <PrimaryScorePill/> (optional)
  metricsRow,         // row of chips (Interviews + other metrics)
  body,               // reasons list or extra block above receipts
  receipts,           // <ReceiptsList/>
  onDrillDown,        // optional (fallback action if no link supplied)
  linkTo,             // ⬅️ NEW: in-app route target, e.g. `/job/some-slug?...`
  href                // ⬅️ NEW: external/absolute URL (or same as linkTo)
}) {
  const [open, setOpen] = React.useState(false);
  const handleDrill = () => setOpen(true);
  const close = () => setOpen(false);

  // Prefer in-app route (SPA), then href; otherwise fall back to placeholder dialog
  const hasInAppLink = !!linkTo;
  const hasHref = !!href;

  // Copy link convenience for the bottom rail (enabled when we have a link)
  const copyableLink = linkTo || href || '';
  const handleCopy = async () => {
    try {
      if (copyableLink) await navigator.clipboard.writeText(copyableLink);
    } catch (_) {}
  };

  return (
    <>
      <Card variant="outlined" sx={{ height: '100%' }}>
        <CardContent>
          {/* Title row with link-out at top-right */}
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>

            {/* ⬇️ UPDATED: link-out behavior */}
            {hasInAppLink ? (
              <Tooltip title="Open details">
                <IconButton size="small" component={Link} to={linkTo} aria-label="Open details">
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : hasHref ? (
              <Tooltip title="Open details">
                <IconButton size="small" component="a" href={href} target="_blank" rel="noopener" aria-label="Open details">
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title="Open details">
                <IconButton size="small" onClick={onDrillDown || handleDrill} aria-label="Open details">
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>

          {/* Justification directly under title (when present) */}
          {justification && (
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
              {justification}
            </Typography>
          )}

          {/* Primary score pill (Win/Gap/Pos/Neg) */}
          {primaryScore && <Box sx={{ mt: 1 }}>{primaryScore}</Box>}

          {/* Metrics row (Interviews + other chips) */}
          {metricsRow && <Box sx={{ mt: 1 }}>{metricsRow}</Box>}

          {/* Reasons / extra body */}
          {body && <Box sx={{ mt: 1 }}>{body}</Box>}

          {/* Receipts */}
          {receipts && <Box sx={{ mt: 1 }}>{receipts}</Box>}

          <Divider sx={{ my: 1.25 }} />

          {/* Bottom rail: left = type + variant; right = secondary actions */}
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={1} alignItems="center">
              {typePill && <CategoryPill category={typePill} />}
              {variantPill}
            </Stack>

            {/* ⬇️ Enable “Copy link” when we actually have a link */}
            <Stack direction="row" spacing={0.5} sx={{ opacity: 0.5, transition: 'opacity .15s' }} className="tile-actions">
              <Tooltip title={copyableLink ? "Copy link" : "Copy link (coming soon)"}>
                <span>
                  <IconButton size="small" disabled={!copyableLink} onClick={handleCopy}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Share to Slack (coming soon)">
                <span><IconButton size="small" disabled><ShareIcon fontSize="small" /></IconButton></span>
              </Tooltip>
              <Tooltip title="Add to report (coming soon)">
                <span><IconButton size="small" disabled><AddIcon fontSize="small" /></IconButton></span>
              </Tooltip>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Placeholder dialog (only used when there is no link target) */}
      <Dialog open={open} onClose={close}>
        <DialogTitle>Details coming soon</DialogTitle>
        <DialogContent>
          This is a placeholder for a fuller view (quotes, trends, and breakdowns). We’ll wire this later.
        </DialogContent>
      </Dialog>

      <style>{`
        .tile-actions:hover { opacity: 1 !important; }
        .MuiCard-root:hover .tile-actions { opacity: 1 !important; }
      `}</style>
    </>
  );
}
